import { Box, FormControl, InputLabel, MenuItem, Select, Tab, TextField, Typography } from '@mui/material'
import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import { fileUrl } from 'api/api'
import TreatmentHistoryApi from 'api/dentists/treatment-history.api'
import HistoryTreatmentApi from 'api/historyTreatment/historyTreatmentApi'

/** CONSTANTS */
import { Icons, imgs, onImgError } from 'constants/images'

/** COMPONENT */
import EmrDx from 'component/Treatments/PopupEmrOverview/EmrDx'
import EmrTx from 'component/Treatments/PopupEmrOverview/EmrTx'
import EmrTxc from 'component/Treatments/PopupEmrOverview/EmrTxc'
import EmrTxp from 'component/Treatments/PopupEmrOverview/EmrTxp'
import MainButton from 'new-components/buttons/main-button'

/** STYLE */
import * as UseStyled from 'component/Treatments/PopupEmrOverview/UseStyled'
import ToothChart from 'component/ToothChart/ToothChart'
import PageModal from 'new-components/modal/page-modal'

/** FDI 11-18, 21-28, 31-38, 41-48 */
const FDI_REGEX = /^(1[1-8]|2[1-8]|3[1-8]|4[1-8])$/
function parseToothNumbersFromInput(input: string): number[] {
  if (!input || !input.trim()) return []
  const tokens = input.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
  const numbers: number[] = []
  tokens.forEach((token) => {
    const match = token.match(/(\d{2})/)
    if (match) {
      const num = parseInt(match[1], 10)
      if (FDI_REGEX.test(match[1])) numbers.push(num)
    }
  })
  return _.uniq(numbers)
}

function parseTeethString(teeth: string | null | undefined): { toothNumbers: number[]; surfaces: string[] } {
  if (!teeth || typeof teeth !== 'string') return { toothNumbers: [], surfaces: [] }
  const tokens = teeth.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
  const toothNumbers: number[] = []
  const surfaces: string[] = []
  tokens.forEach((token) => {
    const match = token.match(/(\d{2})([MDOBFLP])?/i)
    if (match) {
      const num = parseInt(match[1], 10)
      if (FDI_REGEX.test(match[1])) toothNumbers.push(num)
      if (match[2]) surfaces.push(match[2].toUpperCase())
    } else if (/^[MDOBFLP]$/i.test(token)) {
      surfaces.push(token.toUpperCase())
    }
  })
  return { toothNumbers: _.uniq(toothNumbers), surfaces: _.uniq(surfaces) }
}

const SURFACE_ALIAS: Record<string, string[]> = {
  B: ['B', 'F'],
  F: ['B', 'F'],
  L: ['L', 'P'],
  P: ['L', 'P']
}
function recordMatchesSurface(recordSurfaces: string[], filterSurface: string): boolean {
  if (!filterSurface) return true
  const allowed = SURFACE_ALIAS[filterSurface] || [filterSurface]
  // if (recordSurfaces.length === 0) return true
  return recordSurfaces.some((s) => allowed.includes(s))
}

const SURFACE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_ALL' },
  { value: 'M', labelKey: 'TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_M' },
  { value: 'D', labelKey: 'TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_D' },
  { value: 'O', labelKey: 'TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_O' },
  { value: 'B', labelKey: 'TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_BF' },
  { value: 'L', labelKey: 'TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_LP' }
]

interface IPopupEmrOverviewType {
  patientId: number
  onClose: () => void
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

export interface ItemRender {
  date: string
  treatment: any
  diagnosis: any
  treatmentCourse: any
  treatmentPlan: any
}

export interface TreatmentPanelProps {
  treatments: ItemRender[]
}

export type AppliedFilter = { toothNumbers: number[]; surface: string }

export default function PopupEmrOverview(props: IPopupEmrOverviewType) {
  const { t } = useTranslation()
  const tabs: string[] = ['TX.POPUP_EMR_OVERVIEW.TABS.TX', 'TX.POPUP_EMR_OVERVIEW.TABS.DX', 'TX.POPUP_EMR_OVERVIEW.TABS.TXP', 'TX.POPUP_EMR_OVERVIEW.TABS.TXC'];
  const [activeTab, setActiveTab] = useState<number>(0);
  const [treatments, setTreatments] = useState<ItemRender[]>([]);
  const [image, setImage] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingToothChart, setLoadingToothChart] = useState(false);
  const [latestPatientCaseId, setLatestPatientCaseId] = useState<number | undefined>(undefined)
  const [teethType, setTeethType] = useState<string>('ADULT')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const [searchToothInput, setSearchToothInput] = useState('')
  const [surfaceFilter, setSurfaceFilter] = useState('')
  const [appliedFilter, setAppliedFilter] = useState<AppliedFilter | null>(null)

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleSearch = () => {
    const toothNumbers = parseToothNumbersFromInput(searchToothInput)

    console.log('toothNumbers', toothNumbers)
    setAppliedFilter({ toothNumbers, surface: surfaceFilter })
  }

  const handleClearSearch = () => {
    setSearchToothInput('')
    setSurfaceFilter('')
    setAppliedFilter(null)
  }

  const filteredTreatments = useMemo(() => {
    const { toothNumbers, surface } = appliedFilter || { toothNumbers: [], surface: '' }
    const matchRecord = (teethStr: string | null | undefined): boolean => {
      const { toothNumbers: recordTeeth, surfaces: recordSurfaces } = parseTeethString(teethStr)
      const toothMatch = recordTeeth.some((tooth) => toothNumbers.includes(tooth))
      const surfaceMatch = recordMatchesSurface(recordSurfaces, surface)

      console.log('toothMatch', toothMatch)
      console.log('searchToothInput', searchToothInput,searchToothInput.length > 0)
      console.log('toothNumbers', toothNumbers)
      console.log('recordTeeth', recordTeeth)
      console.log('surfaceMatch', surfaceMatch)
      return toothMatch && surfaceMatch
    }
    return treatments
      .map((item) => ({
        ...item,
        treatment: (item.treatment || []).filter((tx: any) => searchToothInput.length > 0 ? matchRecord(tx?.teeth) : true),
        diagnosis: (item.diagnosis || []).filter((dx: any) => searchToothInput.length > 0 ? matchRecord(dx?.teeth) : true),
        treatmentPlan: (item.treatmentPlan || []).filter((tp: any) => searchToothInput.length > 0 ? matchRecord(tp?.teeth) : true),
        treatmentCourse: (item.treatmentCourse || []).filter((tc: any) => searchToothInput.length > 0 ? matchRecord(tc?.teeth) : true)
      }))
      .filter((item) => item.treatment?.length > 0 || item.diagnosis?.length > 0 || item.treatmentPlan?.length > 0 || item.treatmentCourse?.length > 0)
  }, [treatments, appliedFilter])

  const displayTreatments = appliedFilter ? filteredTreatments : treatments
  const hasTxData = _.some(displayTreatments, (trm) => !_.isEmpty(trm.treatment))
  const hasDxData = _.some(displayTreatments, (trm) => !_.isEmpty(trm.diagnosis))
  const hasTxpData = _.some(displayTreatments, (trm) => !_.isEmpty(trm.treatmentPlan))
  const hasTxcData = _.some(displayTreatments, (trm) => !_.isEmpty(trm.treatmentCourse))
  const showNoHistoryTooth = appliedFilter != null && !hasTxData && !hasDxData && !hasTxpData && !hasTxcData

  const loadOverview = async () => {
    const resp = await HistoryTreatmentApi.findOverview(props.patientId).finally(() => setLoading(false))
    if (resp.status === 200) {
      const items = resp.data
      const newItems: ItemRender[] = []

      _.map(items, (item, index) => {
        newItems.push({
          date: item.createdAt,
          treatment: [...item.order.orderTreatments, ...item.order.orderProducts],
          diagnosis: item.patientDiagnosis,
          treatmentCourse: item.patientTreatmentCourse,
          treatmentPlan: item.patientTreatmentPlan
        })
      })

      if (!_.isEmpty(resp.data)) {
        const data: any = []
        const item = _.orderBy(resp.data, ['updatedAt'], ['desc', 'asc'])
        _.map(item, (trm: any, index) => {
          if (!_.isEmpty(trm?.order?.orderTreatments)) return data.push(trm?.order?.orderTreatments ? [...trm.order.orderTreatments] : [])
        })
        const caseId = data[0]?.patientCaseId || item[0]?.order?.patientCaseId
        if (caseId) setLatestPatientCaseId(Number(caseId))
        if (!_.isEmpty(data) && props.patientId) loadToothChart(props.patientId, caseId)
      } else {
        return
      }

      setTreatments(_.orderBy(newItems, ['date'], ['desc', 'asc']))
    } else {
      return
    }
  }

  const loadToothChart = async (patientId: number, patientCaseId: number) => {
    setLoadingToothChart(true)
    const resp = await TreatmentHistoryApi.findTreatmentHistoryEmr(Number(patientId), Number(patientCaseId)).finally(() => setLoadingToothChart(false))
    if (resp.status === 200) setImage(resp.data.patientCaseInfo.toothChart)
  }

  const renderImage = (val: any) => {
    return `${fileUrl}/${val.filePath}/${val.fileName}`
  }

  const handleClickBtnMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseBtnMore = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (props.patientId) loadOverview()
  }, [props.patientId]);

  // if (loading || loadingToothChart) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, background: 'rgba(0,0,0,0.1)' }}>
  //       <Loading show={true} type='softLoading' />
  //     </Box>
  //   )
  // }

  return (
    <PageModal
      title={t('TX.POPUP_EMR_OVERVIEW.TITLE')}
      size='L'
      isOpen
      handleClose={props.onClose}
      onCancel={{ onClick: props.onClose }}
      onSubmit={{ onClick: () => null }}
      onCloseSubmit
      overflow
    >
      <UseStyled.CustomPopupEmrOverview>
        <Box>
          {_.some(treatments, (trm) => !_.isEmpty(trm.treatment)) && (
            <Box className='mx-auto mb-4'>
              <ToothChart
                type='treatment'
                teethType={teethType}
                patientId={props.patientId}
                patientCaseId={latestPatientCaseId}
                isViewOnly={true}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                <MainButton
                  title={t('TX.POPUP_EMR_OVERVIEW.TOOTH_TYPE.TOOTH_ADULT')}
                  type={teethType === 'ADULT' ? 'primary' : 'secondary'}
                  variant={teethType === 'ADULT' ? 'contained' : 'outlined'}
                  onClick={() => setTeethType('ADULT')}
                  style={{ minWidth: 80, height: 30, fontSize: 13 }}
                />
                <MainButton
                  title={t('TX.POPUP_EMR_OVERVIEW.TOOTH_TYPE.TOOTH_CHILD')}
                  type={teethType === 'CHILD' ? 'primary' : 'secondary'}
                  variant={teethType === 'CHILD' ? 'contained' : 'outlined'}
                  onClick={() => setTeethType('CHILD')}
                  style={{ minWidth: 80, height: 30, fontSize: 13 }}
                />
              </Box>
            </Box>
          )}
          <Box>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label={t('TX.POPUP_EMR_OVERVIEW.SEARCH.TOOTH_LABEL')}
                  placeholder={t('TX.POPUP_EMR_OVERVIEW.SEARCH.TOOTH_PLACEHOLDER')}
                  value={searchToothInput}
                  onChange={(e) => setSearchToothInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{ minWidth: 260 }}
                  inputProps={{ maxLength: 50 }}
                />
                {/* <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel shrink id="popup-emr-surface-label">{t('TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_LABEL')}</InputLabel>
                  <Select
                    labelId="popup-emr-surface-label"
                    label={t('TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_LABEL')}
                    displayEmpty
                    value={surfaceFilter}
                    onChange={(e) => setSurfaceFilter(e.target.value)}
                    renderValue={(v) => (v ? t(SURFACE_OPTIONS.find((o) => o.value === v)?.labelKey || '') : t('TX.POPUP_EMR_OVERVIEW.SEARCH.SURFACE_ALL'))}
                  >
                    {SURFACE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'all'} value={opt.value}>
                        {t(opt.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}
                <MainButton type="primary" variant="contained" title={t('TX.POPUP_EMR_OVERVIEW.SEARCH.SEARCH_BTN')} onClick={handleSearch} style={{ minWidth: 90 }} />
                <MainButton type="secondary" variant="outlined" title={t('TX.POPUP_EMR_OVERVIEW.SEARCH.CLEAR_BTN')} onClick={handleClearSearch} style={{ minWidth: 70 }} />
              </Box>
            </Box>
            <Box>
              <UseStyled.CustomTabsEmr value={activeTab} onChange={handleChangeTab} aria-label="emr overview tabs" scrollButtons="auto" variant="scrollable">
                {_.map(tabs, (tab: string, index: number) => {
                  return <Tab key={index} label={t(tab)} {...a11yProps(index)} />
                })}
              </UseStyled.CustomTabsEmr>
              {showNoHistoryTooth ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">{t('TX.POPUP_EMR_OVERVIEW.SEARCH.NO_HISTORY_TOOTH')}</Typography>
                </Box>
              ) : (
                <>
                  <TabPanel value={activeTab} index={0}>
                    {hasTxData ? <EmrTx treatments={displayTreatments} /> : <NoItem />}
                  </TabPanel>
                  <TabPanel value={activeTab} index={1}>
                    {hasDxData ? <EmrDx treatments={displayTreatments} /> : <NoItem />}
                  </TabPanel>
                  <TabPanel value={activeTab} index={2}>
                    {hasTxpData ? <EmrTxp treatments={displayTreatments} /> : <NoItem />}
                  </TabPanel>
                  <TabPanel value={activeTab} index={3}>
                    {hasTxcData ? <EmrTxc treatments={displayTreatments} /> : <NoItem />}
                  </TabPanel>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </UseStyled.CustomPopupEmrOverview>
    </PageModal>
  )
}

function a11yProps(index: number) {
  return {
    id: `emr-overview-tab-${index}`,
    'aria-controls': `emr-overview-tabpanel-${index}`
  }
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" style={{ height: '500px', overflow: 'scroll' }} hidden={value !== index} id={`emr-overview-tabpanel-${index}`} aria-labelledby={`emr-overview-tab-${index}`} {...other}>
      {value === index && <Box className='mb-4'>{children}</Box>}
    </div>
  )
}

export function NoItem() {
  const { t } = useTranslation()

  return (
    <Typography className='mt-4 text-center'>{t('REGISTER.NO_ITEM')}</Typography>
  )
}